import { useEffect, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { Clock, History, Loader2, PlayCircle, Trash2 } from "lucide-react";
import { Alert, Badge, ConfirmModal, EmptyState, LoadingState, type BadgeTone } from "@/components/ui";
import { cn } from "@/lib/utils";

interface RunInfo {
  run_id: string;
  timestamp: string;
  dataset_version: string;
  agent_name?: string;
  agent_path?: string;
  model?: string;
  total_questions: number;
  success_rate?: number;
  success_count?: number;
  mean_accuracy: number;
  sql_success_rate_pct?: number;
  is_current: boolean;
}

interface LiveRunInfo {
  run_id: string;
  agent_name?: string;
  agent_path?: string;
  model?: string;
  dataset_version?: string;
  total_questions?: number;
  mean_accuracy?: number;
}

interface RunHistoryProps {
  onSelectRun: (runId: string) => void;
  liveRunId?: string | null;
  liveRunInfo?: LiveRunInfo | null;
  currentRunId?: string;
  viewMode?: "live" | "historical";
  onBackToLive?: () => void;
}

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return (
    date.toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) +
    " " +
    date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
};

const formatAgentName = (agentName?: string, agentPath?: string) => {
  if (agentName) return agentName;
  if (agentPath) {
    // Extract agent name from a path like "agents/bigquery_agent_v1/agent.py"
    const match = agentPath.match(/agents\/([^/]+)/);
    return match ? match[1] : agentPath;
  }
  return "Onbekend";
};

const formatModel = (model?: string) => {
  if (!model) return "gemini-2.5-flash";
  return model.replace("gemini-", "").replace("-preview", "");
};

const scoreTone = (score: number): BadgeTone => (score >= 4.5 ? "success" : score >= 3.5 ? "warning" : "danger");

function ScoreBadge({ score }: { score: number }) {
  return (
    <Badge tone={scoreTone(score)} className="font-semibold tabular-nums" title="Gemiddelde score">
      {score.toFixed(2)}
    </Badge>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</div>;
}

function LiveDot() {
  return (
    <span className="relative flex h-2 w-2 shrink-0">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sl-red opacity-60" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-sl-red" />
    </span>
  );
}

function RunItem({
  selected,
  onSelect,
  children,
  className,
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
  className?: string;
}) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={cn(
        "group relative cursor-pointer rounded-md border-l-[3px] px-3 py-2.5 transition-colors",
        selected ? "border-l-sl-yellow bg-accent" : "border-l-transparent hover:bg-muted",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function RunHistory({ onSelectRun, liveRunId, liveRunInfo, currentRunId, viewMode, onBackToLive }: RunHistoryProps) {
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchRuns = () => {
    fetch("/api/evaluation/runs")
      .then((response) => response.json())
      .then((data) => {
        setRuns(data.runs || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch runs:", error);
        setLoading(false);
      });
  };

  // Ask for confirmation (was window.confirm)
  const handleDelete = (runId: string, e: MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onSelectRun
    setConfirmDeleteId(runId);
  };

  const performDelete = async () => {
    const runId = confirmDeleteId;
    if (!runId) return;
    setConfirmDeleteId(null);
    setDeleteError(null);
    setDeleting(runId);
    try {
      const response = await fetch(`/api/evaluation/runs/${runId}`, { method: "DELETE" });
      if (response.ok) {
        setRuns((prev) => prev.filter((r) => r.run_id !== runId));
        console.log(`Deleted run ${runId}`);
      } else {
        const error = await response.json();
        setDeleteError(`Verwijderen mislukt: ${error.detail || "onbekende fout"}`);
      }
    } catch (error) {
      console.error("Failed to delete run:", error);
      setDeleteError("Run kon niet worden verwijderd");
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    fetchRuns();
  }, []);

  // Refresh the list when an evaluation completes
  useEffect(() => {
    const handleEvaluationComplete = () => fetchRuns();
    window.addEventListener("evaluation-complete", handleEvaluationComplete);
    return () => window.removeEventListener("evaluation-complete", handleEvaluationComplete);
  }, []);

  if (loading) {
    return <LoadingState label="Runs laden…" />;
  }

  // The live run is shown separately
  const historicalRuns = runs.filter((r) => r.run_id !== liveRunId);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex shrink-0 items-center justify-between px-1">
        <h2 className="text-sm font-semibold">Runs</h2>
        {historicalRuns.length > 0 && <span className="text-xs text-muted-foreground tabular-nums">{historicalRuns.length}</span>}
      </div>

      {deleteError && (
        <Alert tone="danger" onClose={() => setDeleteError(null)} className="mb-3 px-3 py-2 text-xs">
          {deleteError}
        </Alert>
      )}

      {liveRunId && viewMode === "historical" && onBackToLive && (
        <button
          onClick={onBackToLive}
          className="mb-3 flex w-full shrink-0 items-center justify-center gap-2 rounded-md border border-sl-charcoal/15 bg-sl-charcoal px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-sl-charcoal/90"
        >
          <LiveDot />
          Terug naar live run
          <PlayCircle className="h-4 w-4" />
        </button>
      )}

      <div className="flex-1 space-y-4 overflow-y-auto">
        {liveRunId && (
          <div>
            <SectionLabel>
              <LiveDot />
              Live
            </SectionLabel>
            <RunItem selected={viewMode === "live"} onSelect={() => onSelectRun(liveRunId)}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold">{formatAgentName(liveRunInfo?.agent_name, liveRunInfo?.agent_path)}</span>
                {liveRunInfo?.mean_accuracy !== undefined && liveRunInfo.mean_accuracy > 0 ? (
                  <ScoreBadge score={liveRunInfo.mean_accuracy} />
                ) : (
                  <Badge tone="danger">Bezig</Badge>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                <span className="font-mono">{formatModel(liveRunInfo?.model)}</span>
                <span aria-hidden>·</span>
                <span>{liveRunInfo?.dataset_version || "v1.0"}</span>
                {liveRunInfo?.total_questions ? (
                  <>
                    <span aria-hidden>·</span>
                    <span>{liveRunInfo.total_questions} vragen</span>
                  </>
                ) : null}
              </div>
            </RunItem>
          </div>
        )}

        {historicalRuns.length > 0 && (
          <div>
            <SectionLabel>
              <History className="h-3 w-3" />
              Eerdere runs
            </SectionLabel>
            <div className="space-y-0.5">
              {historicalRuns.map((run) => (
                <RunItem key={run.run_id} selected={viewMode === "historical" && currentRunId === run.run_id} onSelect={() => onSelectRun(run.run_id)}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold" title={run.agent_path}>
                      {formatAgentName(run.agent_name, run.agent_path)}
                    </span>
                    <ScoreBadge score={run.mean_accuracy || 0} />
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                    <span className="font-mono">{formatModel(run.model)}</span>
                    <span aria-hidden>·</span>
                    <span>{run.dataset_version}</span>
                    <span aria-hidden>·</span>
                    <span>{run.total_questions} vragen</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatTimestamp(run.timestamp)}
                    </span>
                    <button
                      onClick={(e) => handleDelete(run.run_id, e)}
                      disabled={deleting === run.run_id}
                      className={cn(
                        "rounded p-1 text-muted-foreground transition-opacity hover:bg-[#FDECEC] hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-100",
                        deleting === run.run_id ? "opacity-100" : "opacity-0",
                      )}
                      title="Run verwijderen"
                      aria-label={`Run ${run.run_id} verwijderen`}
                    >
                      {deleting === run.run_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </RunItem>
              ))}
            </div>
          </div>
        )}

        {runs.length === 0 && !liveRunId && (
          <EmptyState icon={<History className="h-5 w-5" />} title="Nog geen runs" description="Start een evaluatie om hier de geschiedenis te zien." />
        )}
      </div>

      <ConfirmModal
        open={confirmDeleteId !== null}
        tone="danger"
        title="Run verwijderen"
        message={
          <>
            Weet je zeker dat je run <span className="break-all font-mono font-semibold text-foreground">{confirmDeleteId}</span> wilt verwijderen? Dit kan niet
            ongedaan worden gemaakt.
          </>
        }
        confirmLabel="Verwijderen"
        onConfirm={performDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}
